import { motion } from 'framer-motion'
import { Trophy, Skull, Handshake, RotateCcw, LogOut, XCircle } from 'lucide-react'

const REASON_LABELS = {
  checkmate: 'PROTOCOL: CHECKMATE',
  stalemate: 'STATUS: STALEMATE',
  draw: 'STATUS: DRAW',
  threefold_repetition: 'REPETITION_LIMIT',
  insufficient_material: 'INSUFFICIENT_FORCE',
  fifty_move_rule: 'EXHAUSTION_RULE',
}

export default function GameOverModal({ gameOver, playerColor, isCreator, onRestart, onLeave, onClose }) {
  const { reason, winner } = gameOver
  const result = !winner ? 'draw' : winner === playerColor ? 'win' : 'loss'

  const config = {
  win: {
    label: 'VICTORY',
    sub: 'The king has fallen. The board is yours.',
    icon: <Trophy className="text-orange-500" size={48} />,
    accent: 'border-orange-500/50 shadow-orange-500/20'
  },
  loss: {
    label: 'DEFEATED',
    sub: 'Your position crumbled. Study the loss.',
    icon: <Skull className="text-zinc-500" size={48} />,
    accent: 'border-zinc-800 shadow-black'
  },
  draw: {
    label: 'STALEMATE',
    sub: 'Neither king could be cornered. Honors even.',
    icon: <Handshake className="text-zinc-400" size={48} />,
    accent: 'border-zinc-700 shadow-white/5'
  }
}

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className={`relative max-w-sm w-full bg-zinc-950 border ${config[result].accent} rounded-2xl overflow-hidden shadow-2xl`}
      >
        {/* Background Decorative Grid */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[grid] bg-[size:20px_20px]" 
             style={{ backgroundImage: 'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)' }} />

        <div className="relative z-10 pt-10 pb-6 px-8 text-center">
          {/* Result Icon */}
          <motion.div 
            initial={{ scale: 0 }} 
            animate={{ scale: 1 }} 
            transition={{ type: 'spring', delay: 0.2 }}
            className="inline-flex p-4 bg-zinc-900 rounded-3xl border border-white/5 mb-6"
          >
            {config[result].icon}
          </motion.div>

          <h2 className="text-5xl font-black italic tracking-tighter uppercase mb-2">
            {config[result].label}
          </h2>
          <p className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-6">
            {REASON_LABELS[reason] || reason}
          </p>
          
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-white/10 to-transparent mb-6" />
          
          <p className="text-zinc-400 text-sm leading-relaxed italic">
            "{config[result].sub}"
          </p>
        </div>

        {/* Actions */}
        <div className="p-6 bg-zinc-900/50 flex flex-col gap-3">
          {isCreator ? (
            <button
              onClick={onRestart}
              className="group relative flex items-center justify-center gap-2 w-full py-4 bg-orange-600 hover:bg-orange-500 text-white rounded-xl font-black uppercase italic tracking-tight transition-all active:scale-95"
            >
              <RotateCcw size={18} className="group-hover:rotate-180 transition-transform duration-500" />
              Initialize Rematch
            </button>
          ) : (
            <div className="py-4 text-center border border-dashed border-white/10 rounded-xl">
               <span className="text-[10px] font-mono text-zinc-500 animate-pulse uppercase tracking-widest">
                 Awaiting Host Command...
               </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mt-2">
            <button onClick={onLeave} className="flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-bold uppercase transition-colors">
              <LogOut size={14} /> Leave
            </button>
            {isCreator && (
              <button onClick={onClose} className="flex items-center justify-center gap-2 py-3 border border-red-900/30 hover:bg-red-950/30 text-red-500 rounded-lg text-xs font-bold uppercase transition-colors">
                <XCircle size={14} /> Kill Room
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}